import { useState, useCallback } from 'react';
import { PAYMENT_STATUS } from '../types/index';

/**
 * State machine for payment lifecycle.
 * Transitions: IDLE → PROCESSING → SUCCESS | FAILED | CANCELLED
 *
 * @returns {{
 *   status: string,
 *   errorMessage: string|null,
 *   retryCount: number,
 *   startProcessing: () => void,
 *   setSuccess: () => void,
 *   setFailed: (msg?: string) => void,
 *   setCancelled: () => void,
 *   reset: () => void,
 *   retry: () => void,
 *   isIdle: boolean,
 *   isProcessing: boolean,
 *   isSuccess: boolean,
 *   isFailed: boolean,
 *   isCancelled: boolean,
 * }}
 */
export function usePaymentState() {
    const [status, setStatus] = useState(PAYMENT_STATUS.IDLE);
    const [errorMessage, setErrorMessage] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const startProcessing = useCallback(() => {
        setStatus(PAYMENT_STATUS.PROCESSING);
        setErrorMessage(null);
    }, []);

    const setSuccess = useCallback(() => {
        setStatus(PAYMENT_STATUS.SUCCESS);
        setErrorMessage(null);
    }, []);

    const setFailed = useCallback((msg = 'Payment failed. Please try again.') => {
        setStatus(PAYMENT_STATUS.FAILED);
        setErrorMessage(msg);
    }, []);

    const setCancelled = useCallback(() => {
        setStatus(PAYMENT_STATUS.CANCELLED);
        setErrorMessage(null);
    }, []);

    const reset = useCallback(() => {
        setStatus(PAYMENT_STATUS.IDLE);
        setErrorMessage(null);
        setRetryCount(0);
    }, []);

    const retry = useCallback(() => {
        setRetryCount((c) => c + 1);
        setStatus(PAYMENT_STATUS.IDLE);
        setErrorMessage(null);
    }, []);

    return {
        status,
        errorMessage,
        retryCount,
        startProcessing,
        setSuccess,
        setFailed,
        setCancelled,
        reset,
        retry,
        isIdle: status === PAYMENT_STATUS.IDLE,
        isProcessing: status === PAYMENT_STATUS.PROCESSING,
        isSuccess: status === PAYMENT_STATUS.SUCCESS,
        isFailed: status === PAYMENT_STATUS.FAILED,
        isCancelled: status === PAYMENT_STATUS.CANCELLED,
    };
}
